'use client';

/**
 * `useVerifyX` — board-scoped X verification with two transports.
 *
 * The hook starts the OAuth 2.0 PKCE round-trip at
 * `/api/auth/twitter/start` and surfaces the resulting handle to the
 * caller. Two transport modes share the same backend:
 *
 *   - **Popup** (desktop default): `window.open` a small centred
 *     window. The callback page `postMessage`s the verified handle
 *     back to the opener and closes itself.
 *   - **Redirect** (mobile / in-app webviews): `window.location.assign`
 *     same-tab to a redirect-mode `start` URL. The callback page
 *     302s back to the original page with `?x_verified=<handle>` (or
 *     `?x_verify_error=…`). The `VerifyXField` component reads those
 *     params on mount via `consumeVerifyXReturnFromUrl`.
 *
 * Why two modes? Mobile browsers — and especially the in-app
 * webviews used by wallet apps (Privy, MetaMask, Coinbase, Rainbow,
 * Warpcast/Farcaster, Trust, Rabby) and social/messaging apps
 * (Instagram, Twitter, FB, Line) — block `window.open` even when
 * called synchronously from a click handler. Falling back to a
 * full-page redirect makes verification reachable in those
 * environments. Desktop keeps the popup so the user doesn't lose
 * their place in the form to a navigation.
 *
 * Tokens never touch the client in either mode — the proof is
 * persisted server-side in `verified_x_handles` and the page
 * re-fetches `/api/auth/twitter/handles` on mount to decorate the
 * pill.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface VerifyXResult {
  username: string;
  avatarUrl?: string;
}

export interface UseVerifyX {
  /**
   * Open the verification flow.
   *
   * - Popup mode: resolves with the verified handle, rejects if the
   *   user closes the popup or X reports an error. Concurrent calls
   *   reuse the in-flight popup.
   * - Redirect mode: navigates the current tab away. The returned
   *   promise never resolves (the page is unloading); callers should
   *   be prepared to receive the result on the *next* page load via
   *   `consumeVerifyXReturnFromUrl`.
   */
  start: () => Promise<VerifyXResult>;
  /** True while a popup is open and we're awaiting a `postMessage`. */
  pending: boolean;
}

interface PendingResolver {
  resolve: (r: VerifyXResult) => void;
  reject: (err: Error) => void;
}

const POPUP_NAME = 'verify-x';
// Popup is small and centred; matches the size used by Privy's own
// social-login popups so the user experience is familiar.
const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

// ---------------------------------------------------------------------------
// Capability detection
// ---------------------------------------------------------------------------

/**
 * True when the environment can reliably open a popup. We default to
 * popup on desktop browsers and fall back to redirect on:
 *   - Coarse-pointer devices (phones, tablets — including iPad pro)
 *   - Known in-app webviews (wallet/social/messaging apps that ship a
 *     WKWebView/Custom Tab and either block `window.open` outright or
 *     handle it so badly that the close-tab/postMessage handshake
 *     can't complete).
 *   - iOS standalone PWA (Add-to-Home-Screen) — `window.open` opens
 *     mobile Safari instead of a popup, breaking the opener
 *     relationship.
 *
 * False positives here are cheap (a desktop user with "Twitter" in
 * their UA gets the redirect flow, which is fully functional, just
 * slightly different UX). False negatives are expensive (a mobile
 * user gets a popup-blocked error and can't proceed at all). We err
 * toward redirect.
 */
function shouldUsePopup(): boolean {
  if (typeof window === 'undefined') return false;

  // Coarse pointer = touch primary input. Catches every phone and
  // most tablets across iOS/Android/Windows.
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  ) {
    return false;
  }

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent ?? '' : '';
  const inAppWebview = new RegExp(
    [
      // Wallets
      'MetaMask',
      'CoinbaseWallet',
      'TrustWallet',
      'imToken',
      'Rainbow',
      'Rabby',
      // Social / messaging in-app browsers
      'FBAN',
      'FBAV',
      'FB_IAB',
      'Instagram',
      'Line\\/',
      'MicroMessenger',
      'Twitter for',
      'TwitterAndroid',
      'Warpcast',
      'Farcaster',
      // Privy embedded auth webview
      'Privy',
    ].join('|'),
    'i',
  );
  if (inAppWebview.test(ua)) return false;

  // iOS standalone PWA: `window.open` becomes "open in Safari", which
  // tears the user out of the app and breaks the opener handshake.
  // The standalone flag is iOS-only and a no-op everywhere else.
  if (
    'standalone' in navigator &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  ) {
    return false;
  }

  return true;
}

/**
 * Build the `start` URL for redirect mode. Encodes the current
 * page's path + query (no origin, no hash) as `return_to` so the
 * callback can 302 us back to the exact step the user was on. The
 * server re-validates `return_to` (`validateReturnTo`) before trusting
 * it for the redirect, so this client construction is allowed to be
 * minimal — the server is the security boundary.
 */
function buildRedirectStartUrl(): string {
  const path = window.location.pathname + window.location.search;
  const params = new URLSearchParams({
    mode: 'redirect',
    return_to: path,
  });
  return `/api/auth/twitter/start?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useVerifyX(): UseVerifyX {
  const [pending, setPending] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const resolverRef = useRef<PendingResolver | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    popupRef.current = null;
    resolverRef.current = null;
    setPending(false);
  }, []);

  // Single mounted listener so concurrent re-renders don't accumulate
  // handlers. The handler resolves whichever flow is currently
  // pending (only one popup at a time, see `start`). No-op in redirect
  // mode — the postMessage never arrives because the popup never
  // opens.
  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      // Strict origin check — reject anything from off-site so a
      // malicious tab can't spoof a verification.
      if (ev.origin !== window.location.origin) return;
      const data = ev.data;
      if (!data || typeof data !== 'object' || data.source !== 'verify-x') return;

      const resolver = resolverRef.current;
      if (!resolver) return;
      if (data.ok) {
        resolver.resolve({ username: data.username, avatarUrl: data.avatarUrl });
      } else {
        resolver.reject(new Error(data.error || 'Verification failed.'));
      }
      cleanup();
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [cleanup]);

  const start = useCallback(() => {
    return new Promise<VerifyXResult>((resolve, reject) => {
      // ----- Redirect transport (mobile / in-app webviews) -------------
      if (!shouldUsePopup()) {
        // Synchronous navigation inside the click handler — no async
        // await before this point, so the user-gesture token still
        // permits a same-tab top-level navigation. The promise never
        // resolves; the next page load will surface the result via
        // `consumeVerifyXReturnFromUrl`.
        try {
          setPending(true);
          window.location.assign(buildRedirectStartUrl());
        } catch (err) {
          // `assign` can only throw under exotic CSP / sandbox rules;
          // surface the failure rather than leaving `pending` stuck.
          setPending(false);
          reject(
            err instanceof Error
              ? err
              : new Error('Could not start X verification.'),
          );
        }
        return;
      }

      // ----- Popup transport (desktop) ---------------------------------
      // Reuse an existing popup if it's still open — avoid the user
      // ending up with two parallel OAuth flows pointed at the same
      // state cookie (the second would trample the first's verifier).
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.focus();
        // Still hand the new resolver in so the (single) postMessage
        // delivers to the most recent caller.
        resolverRef.current = { resolve, reject };
        return;
      }

      const left = Math.max(0, window.screenX + (window.outerWidth - POPUP_WIDTH) / 2);
      const top = Math.max(0, window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2);
      const features = [
        `width=${POPUP_WIDTH}`,
        `height=${POPUP_HEIGHT}`,
        `left=${left}`,
        `top=${top}`,
        'menubar=no',
        'toolbar=no',
        'location=no',
        'status=no',
        'resizable=yes',
        'scrollbars=yes',
      ].join(',');

      const popup = window.open(
        '/api/auth/twitter/start',
        POPUP_NAME,
        features,
      );
      if (!popup) {
        // Last-resort fallback: a desktop browser with popups disabled
        // by user preference (rare in 2026 outside of locked-down
        // enterprise deployments). Take the redirect path so the user
        // isn't stranded with a "popup blocked" message they can't
        // act on without leaving the page anyway.
        try {
          setPending(true);
          window.location.assign(buildRedirectStartUrl());
        } catch {
          setPending(false);
          reject(new Error('Popup blocked. Allow popups and try again.'));
        }
        return;
      }

      popupRef.current = popup;
      resolverRef.current = { resolve, reject };
      setPending(true);

      // Polling fallback — `postMessage` is the happy path, but if the
      // user closes the popup without completing OAuth (e.g. clicks
      // the OS close button on the popup window) we never get a
      // `message` event. Detect closure so the caller's promise
      // doesn't hang forever.
      pollRef.current = setInterval(() => {
        if (popup.closed) {
          const r = resolverRef.current;
          cleanup();
          r?.reject(new Error('Verification cancelled.'));
        }
      }, 500);
    });
  }, [cleanup]);

  return { start, pending };
}

// ---------------------------------------------------------------------------
// Return-from-redirect helpers
// ---------------------------------------------------------------------------

export interface ConsumedVerifyResult {
  /**
   * Verified handle the callback redirected back with, if any.
   * Always sanitised — non-conforming values resolve to `null` so
   * the caller can't be tricked into rendering a malformed handle.
   */
  username: string | null;
  /**
   * Error message the callback redirected back with, if any. Empty
   * means no error. The server caps this at 160 chars before placing
   * it in the URL.
   */
  error: string | null;
  /**
   * `true` iff at least one verify-related param was present and
   * stripped. Use this to decide whether to call `history.replaceState`
   * on the cleaned URL.
   */
  consumed: boolean;
  /**
   * Pre-built clean URL with `x_verified` and `x_verify_error`
   * removed, suitable for `history.replaceState`. Includes pathname,
   * remaining query, and hash.
   */
  cleanUrl: string;
}

/**
 * X usernames are 1–15 chars of `[A-Za-z0-9_]`. Mirrored client-side
 * so we never trust an `x_verified` param the user could have hand-
 * crafted in the URL bar. (The server already wrote a real proof to
 * `verified_x_handles`; this guard is purely about not letting a
 * spoofed URL inject garbage into the wizard's `twitterHandle`
 * field.)
 */
const HANDLE_REGEX = /^[A-Za-z0-9_]{1,15}$/;

/**
 * Read and clear verify-result query params from a URL. Used by
 * `VerifyXField` on mount in redirect transport: the page lands at
 * `…?x_verified=alice` (or `…?x_verify_error=…`), the field
 * applies the value to its parent's state, and replaces the URL with
 * the cleaned variant so a refresh doesn't re-apply the same result.
 *
 * Pure function — accepts any URL-shaped string and returns a
 * descriptive object. Doesn't touch `window.location` itself; the
 * caller does that with `history.replaceState`.
 */
export function consumeVerifyXReturnFromUrl(
  href: string,
): ConsumedVerifyResult {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { username: null, error: null, consumed: false, cleanUrl: href };
  }

  const rawHandle = url.searchParams.get('x_verified');
  const rawError = url.searchParams.get('x_verify_error');
  const consumed = rawHandle !== null || rawError !== null;

  url.searchParams.delete('x_verified');
  url.searchParams.delete('x_verify_error');

  return {
    username: rawHandle && HANDLE_REGEX.test(rawHandle) ? rawHandle : null,
    error: rawError ? rawError : null,
    consumed,
    cleanUrl: url.pathname + url.search + url.hash,
  };
}
